import { LogoutOutlined, UserOutlined } from '@ant-design/icons'
import { Dropdown, Grid, Layout, Menu, Space, Spin, Tag, Typography, type MenuProps } from 'antd'
import { Suspense, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'

import type { Role } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { canAccess } from '../auth/roles'
import { AlertBadge } from './AlertBadge'
import { NAV_ITEMS } from './nav'

const roleColor: Record<Role, string> = { admin: 'red', operator: 'blue', viewer: 'default', transcoder: 'green' }

export function AppLayout() {
  const { user, logout } = useAuth()
  const role = user?.role
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const screens = Grid.useBreakpoint()
  const gutter = screens.md ? 24 : 16

  const items = useMemo(
    () => NAV_ITEMS.filter((i) => (!i.transcoderOnly || role === 'transcoder') && canAccess(role, { min: i.role, transcoder: i.transcoder })).map(({ key, label, icon }) => ({ key, label, icon })),
    [role],
  )
  const selected = items
    .map((i) => i.key)
    .filter((k) => (k === '/' ? pathname === '/' : pathname === k || pathname.startsWith(`${k}/`)))
    .slice(-1)

  const userMenu: MenuProps['items'] = [
    { key: 'logout', icon: <LogoutOutlined />, label: 'Đăng xuất', onClick: logout },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Sider breakpoint="lg" collapsedWidth={0} theme="light" width={220} zeroWidthTriggerStyle={{ top: 12 }}>
        <div style={{ padding: '16px 24px' }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            EVG Transcoder
          </Typography.Title>
        </div>
        <Menu mode="inline" selectedKeys={selected} items={items} onClick={({ key }) => navigate(key)} />
      </Layout.Sider>
      <Layout>
        <Layout.Header
          style={{ background: 'transparent', display: 'flex', justifyContent: 'flex-end', paddingInline: gutter }}
        >
          <Space size="middle">
            {role !== 'transcoder' && <AlertBadge />}
            <Dropdown menu={{ items: userMenu }} trigger={['click']}>
              <Space style={{ cursor: 'pointer' }} data-testid="user-menu">
                <UserOutlined />
                <span>{user?.name || user?.email}</span>
                {user && <Tag color={roleColor[user.role]}>{user.role}</Tag>}
              </Space>
            </Dropdown>
          </Space>
        </Layout.Header>
        <Layout.Content style={{ padding: gutter }}>
          <Suspense
            fallback={
              <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
                <Spin size="large" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </Layout.Content>
      </Layout>
    </Layout>
  )
}
